import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';

import { DateOperator, RelativeDirection, RelativeUnit } from '../../../../../apis/graphql/types';
import { MAX_RELATIVE_VALUE, MIN_RELATIVE_VALUE } from './relativeDateBounds';
import type { ComparisonDateOperator, RelativeDateFilter } from './types';

/**
 * Display-only mirror of campaign-searcher's range math. Keep in lockstep with FilterUtil.java
 * (`dateOperatorRange()` and its `next*`/`past*`/`startOfPeriod`/`endOfPeriod` helpers).
 */

const DATE_FORMAT = 'yyyy-MM-dd';
const DISPLAY_FORMAT = 'MM/dd/yyyy';

/** Backend uses `DayOfWeek.SUNDAY` (FilterUtil.java `WEEK_START`). */
const WEEK_STARTS_ON = 0;

export type RelativeRange = { start: Date; end: Date };

type RelativeRangeInput = Pick<RelativeDateFilter, 'direction' | 'value' | 'unit'> & {
  referenceDate: string | null | undefined;
};

/** Today as YYYY-MM-DD in the user's local time zone. Not `toISOString()`, which shifts to UTC. */
export const todayLocalIso = (now: Date = new Date()): string => format(now, DATE_FORMAT);

/** Parses YYYY-MM-DD as local midnight. Not `new Date(str)`, which parses as UTC midnight. */
const parseLocalIso = (value: string): Date | null => {
  const parsed = parse(value, DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

const startOfPeriod = (date: Date, unit: RelativeUnit): Date => {
  switch (unit) {
    case RelativeUnit.Day:
      return date;
    case RelativeUnit.Week:
      return startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
    case RelativeUnit.Month:
      return startOfMonth(date);
    case RelativeUnit.Quarter:
      return startOfQuarter(date);
    case RelativeUnit.Year:
      return startOfYear(date);
  }
};

const endOfPeriod = (date: Date, unit: RelativeUnit): Date => {
  switch (unit) {
    case RelativeUnit.Day:
      return date;
    case RelativeUnit.Week:
      return addDays(startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON }), 6);
    case RelativeUnit.Month:
      return endOfMonth(date);
    case RelativeUnit.Quarter:
      return subDays(addMonths(startOfQuarter(date), 3), 1);
    case RelativeUnit.Year:
      return subDays(addYears(startOfYear(date), 1), 1);
  }
};

const startOfNextPeriod = (today: Date, unit: RelativeUnit): Date => {
  switch (unit) {
    case RelativeUnit.Day:
      return addDays(today, 1);
    case RelativeUnit.Week:
      return addWeeks(startOfWeek(today, { weekStartsOn: WEEK_STARTS_ON }), 1);
    case RelativeUnit.Month:
      return addMonths(startOfMonth(today), 1);
    case RelativeUnit.Quarter:
      return addMonths(startOfQuarter(today), 3);
    case RelativeUnit.Year:
      return addYears(startOfYear(today), 1);
  }
};

const endOfPreviousPeriod = (today: Date, unit: RelativeUnit): Date => subDays(startOfPeriod(today, unit), 1);

// --- NEXT ---

// "Next N days" includes today; every other unit starts at the next period boundary.
const nextRangeStart = (today: Date, unit: RelativeUnit): Date =>
  unit === RelativeUnit.Day ? today : startOfNextPeriod(today, unit);

const nextRangeEnd = (today: Date, value: number, unit: RelativeUnit): Date => {
  if (unit === RelativeUnit.Day) return addDays(today, value);

  const start = startOfNextPeriod(today, unit);
  switch (unit) {
    case RelativeUnit.Week:
      return subDays(addWeeks(start, value), 1);
    case RelativeUnit.Month:
      return subDays(addMonths(start, value), 1);
    case RelativeUnit.Quarter:
      return subDays(addMonths(start, value * 3), 1);
    case RelativeUnit.Year:
      return subDays(addYears(start, value), 1);
    default:
      return start;
  }
};

// --- PAST ---

const pastRangeStart = (today: Date, value: number, unit: RelativeUnit): Date => {
  switch (unit) {
    case RelativeUnit.Day:
      return subDays(today, value);
    case RelativeUnit.Week:
      return startOfWeek(subWeeks(endOfPreviousPeriod(today, unit), value - 1), { weekStartsOn: WEEK_STARTS_ON });
    case RelativeUnit.Month:
      return subMonths(startOfMonth(today), value);
    case RelativeUnit.Quarter:
      return subMonths(startOfQuarter(today), value * 3);
    case RelativeUnit.Year:
      return subYears(startOfYear(today), value);
  }
};

// "Past N days" includes today; every other unit ends at the last complete period.
const pastRangeEnd = (today: Date, unit: RelativeUnit): Date =>
  unit === RelativeUnit.Day ? today : endOfPreviousPeriod(today, unit);

/** The inclusive `[start, end]` range a relative filter resolves to. Null if invalid; never throws. */
export const computeRelativeRange = ({
  direction,
  value,
  unit,
  referenceDate,
}: RelativeRangeInput): RelativeRange | null => {
  if (!direction || !unit || !referenceDate) return null;

  const today = parseLocalIso(referenceDate);
  if (!today) return null;

  if (direction === RelativeDirection.This) {
    return { start: startOfPeriod(today, unit), end: endOfPeriod(today, unit) };
  }

  if (value == null || !Number.isFinite(value) || value < MIN_RELATIVE_VALUE || value > MAX_RELATIVE_VALUE) return null;

  return direction === RelativeDirection.Next
    ? { start: nextRangeStart(today, unit), end: nextRangeEnd(today, value, unit) }
    : { start: pastRangeStart(today, value, unit), end: pastRangeEnd(today, unit) };
};

/** Collapsed display-box text, e.g. `07/30/2026 - 11/29/2026`. The ASCII `' - '` separator is
 * required — FilterDatePicker.handleChangeRaw parses on it. */
export const formatRelativeRangeDisplay = (filter: RelativeRangeInput): string => {
  const range = computeRelativeRange(filter);
  if (!range) return '';
  return `${format(range.start, DISPLAY_FORMAT)} - ${format(range.end, DISPLAY_FORMAT)}`;
};

export const formatRangeBound = (date: Date): string => format(date, DISPLAY_FORMAT);

/** Selects the single boundary date a comparison operator compares against.
 * Is Before / Is On or After → window start; Is After / Is On or Before → window end. */
export const resolveConditionalBoundary = (
  operator: ComparisonDateOperator,
  filter: RelativeRangeInput
): Date | null => {
  const range = computeRelativeRange(filter);
  if (!range) return null;
  return operator === DateOperator.IsBefore || operator === DateOperator.IsOnOrAfter ? range.start : range.end;
};
